import React, { useId, useState } from "react";
import OfficeToHomeWarning, { officeToHomeConflictMessage } from "./office_to_home_warning";
import OfferedPatientsTooltip from "./offered_patients_tooltip";
import BookedConsultDetails from "./booked_consult_details";
import { selectedSlotFromEntry, type ConsultAvailabilityDate, type ConsultSlotEntry, type SelectedConsultSlot } from "./consult_availability";

interface Props {
  dates: ConsultAvailabilityDate[];
  selectedSlots: SelectedConsultSlot[];
  disabled: boolean;
  emptyMessage: string;
  onDateSelection: (date: ConsultAvailabilityDate, checked: boolean) => void;
  onSlotToggle: (slot: SelectedConsultSlot, checked: boolean) => void;
  onCustomSlot: (date: string, time: string) => void;
}

function minutes(time: string) {
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
}

function timeLabel(value: number) {
  const hour = Math.floor(value / 60) % 24;
  return `${hour % 12 || 12}:${String(value % 60).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}

function dateLabel(date: string) {
  return new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));
}

// Place overlapping office/home alternatives and off-grid custom times in lanes.
function layout(entries: ConsultSlotEntry[]) {
  const positioned: Array<{ entry: ConsultSlotEntry; start: number; end: number; lane: number; lanes: number }> = [];
  let group: typeof positioned = [];
  let laneEnds: number[] = [];
  const finishGroup = () => group.forEach((item) => { item.lanes = laneEnds.length; });

  [...entries].sort((a, b) => minutes(a.time) - minutes(b.time) || a.key.localeCompare(b.key)).forEach((entry) => {
    const start = minutes(entry.time);
    if (laneEnds.length && laneEnds.every((end) => end <= start)) {
      finishGroup();
      group = [];
      laneEnds = [];
    }
    const availableLane = laneEnds.findIndex((end) => end <= start);
    const lane = availableLane < 0 ? laneEnds.length : availableLane;
    const item = { entry, start, end: start + entry.durationMinutes, lane, lanes: 1 };
    laneEnds[lane] = item.end;
    group.push(item);
    positioned.push(item);
  });
  finishGroup();
  return positioned;
}

export default function ConsultAvailabilityCalendar({ dates, selectedSlots, disabled, emptyMessage, onDateSelection, onSlotToggle, onCustomSlot }: Props) {
  const calendarId = useId();
  const [mobileDate, setMobileDate] = useState<string | null>(null);
  const [bookedKey, setBookedKey] = useState<string | null>(null);
  const bookedEntry = dates.flatMap((date) => date.entries).find((entry) => !entry.selectable && entry.key === bookedKey);
  const activeDate = dates.find((date) => date.date === mobileDate)?.date || dates[0]?.date;
  const selectedByKey = new Map(selectedSlots.map((slot) => [slot.key, slot]));
  const days = dates.map((date) => {
    const customSlots = selectedSlots.filter((slot) => slot.custom && slot.date === date.date &&
      !date.entries.some((entry) => entry.key === slot.key || (!entry.selectable && entry.time === slot.time)));
    const entries = [...date.entries, ...customSlots.map((slot) => ({ ...slot, selectable: true, durationMinutes: 30, offeredPatients: [] }))];
    return { ...date, entries, positioned: layout(entries) };
  });
  const entries = days.flatMap((date) => date.positioned);
  const start = Math.floor(Math.min(9 * 60 + 30, ...entries.map((item) => item.start)) / 30) * 30;
  // Include the entire 4:30 PM slot, and extend for appointments outside the default day.
  const end = Math.ceil(Math.max(17 * 60, ...entries.map((item) => item.end)) / 30) * 30;
  const ticks = Array.from({ length: (end - start) / 30 }, (_, index) => start + index * 30);
  const pixelsPerMinute = 1.6;
  const height = (end - start) * pixelsPerMinute;

  if (!dates.length) return <p className="app-copy py-6">{emptyMessage}</p>;

  return (
    <div className="mt-4">
      <div className="mb-3 flex flex-wrap gap-2 md:hidden" aria-label="Visible consult day">
        {dates.map((date) => (
          <button type="button" key={date.date} aria-pressed={activeDate === date.date}
            className={activeDate === date.date ? "app-button-primary" : "app-button-secondary"}
            onClick={() => setMobileDate(date.date)}>{dateLabel(date.date)}</button>
        ))}
      </div>
      <div className="consult-calendar-scroll" role="region" aria-label="Consult availability calendar" tabIndex={0}>
        <div className="consult-calendar-grid" style={{ "--consult-day-count": days.length } as React.CSSProperties}>
          <div className="consult-calendar-header" aria-hidden="true" />
          {days.map((date) => {
            const selectable = date.entries.filter((entry) => entry.selectable);
            const withoutConflicts = selectable.filter((entry) => !officeToHomeConflictMessage(selectedByKey.get(entry.key) || entry));
            const availableTimeCount = new Set(withoutConflicts.map((entry) => minutes(entry.time))).size;
            const selectedCount = selectable.filter((entry) => selectedByKey.has(entry.key)).length;
            const allSelected = selectedCount > 0 && withoutConflicts.every((entry) => selectedByKey.has(entry.key));
            return (
              <div key={date.date} className="consult-calendar-header consult-calendar-day px-2 py-3" data-active={date.date === activeDate}>
                <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-neutral-950 pointer-coarse:min-h-11 dark:text-white">
                  <input type="checkbox" className="h-4 w-4 shrink-0 accent-emerald-600"
                    aria-label={`Select all available times for ${date.label}`}
                    title="Select all skips times with travel conflicts."
                    checked={allSelected}
                    ref={(input) => { if (input) input.indeterminate = selectedCount > 0 && !allSelected; }}
                    disabled={disabled || (withoutConflicts.length === 0 && selectedCount === 0)}
                    onChange={(event) => {
                      if (event.target.checked) onDateSelection(date, true);
                      else selectedSlots.filter((slot) => slot.date === date.date).forEach((slot) => onSlotToggle(slot, false));
                    }} />
                  <span>{dateLabel(date.date)}</span>
                </label>
                <p className="mt-1 pl-6 text-xs text-neutral-500 dark:text-neutral-400">{availableTimeCount} available</p>
              </div>
            );
          })}
          <div className="relative bg-white dark:bg-neutral-900" style={{ height }}>
            {ticks.map((tick) => <span key={tick} className="absolute right-2 text-[11px] tabular-nums text-neutral-500 dark:text-neutral-400" style={{ top: (tick - start) * pixelsPerMinute + 3 }}>{timeLabel(tick)}</span>)}
          </div>
          {days.map((date) => (
            <div key={date.date} className="consult-calendar-timeline consult-calendar-day" data-active={date.date === activeDate}
              aria-label={date.label} style={{ height, "--consult-row-height": `${30 * pixelsPerMinute}px` } as React.CSSProperties}>
              {ticks.filter((tick) => tick < 24 * 60 && !date.positioned.some((entry) => tick < entry.end && tick + 30 > entry.start)).map((tick) => (
                <button key={tick} type="button" disabled={disabled}
                  className="group absolute inset-x-0 mx-0.5 rounded text-xs text-neutral-500 hover:bg-emerald-50 hover:text-emerald-800 focus-visible:bg-emerald-50 focus-visible:text-emerald-800 focus-visible:outline-2 focus-visible:outline-emerald-600 disabled:pointer-events-none dark:text-neutral-400 dark:hover:bg-emerald-950 dark:hover:text-emerald-200 dark:focus-visible:bg-emerald-950 dark:focus-visible:text-emerald-200"
                  style={{ top: (tick - start) * pixelsPerMinute + 2, height: 30 * pixelsPerMinute - 4 }}
                  aria-label={`Add custom consult on ${date.label} at ${timeLabel(tick)}`}
                  aria-haspopup="dialog"
                  onClick={() => onCustomSlot(date.date, `${String(Math.floor(tick / 60)).padStart(2, "0")}:${String(tick % 60).padStart(2, "0")}`)}>
                  <span aria-hidden="true" className="opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 pointer-coarse:opacity-100">+ Add custom</span>
                </button>
              ))}
              {date.positioned.map(({ entry, start: entryStart, lane, lanes }) => {
                const selected = selectedByKey.get(entry.key);
                const slot = (entry.selectable && selected) || selectedSlotFromEntry(date, entry);
                const travelConflict = entry.selectable ? officeToHomeConflictMessage(slot) : null;
                const conflictId = `${calendarId}-${entry.key}-travel-conflict`;
                const locationLabel = slot.devaLocation === "office" ? "Office" : slot.devaLocation === "home" ? "Home" : "Location unknown";
                return (
                  <div key={entry.key} className="absolute p-0.5" style={{ top: (entryStart - start) * pixelsPerMinute, height: entry.durationMinutes * pixelsPerMinute, left: `${lane / lanes * 100}%`, width: `${100 / lanes}%` }}>
                    {entry.selectable ? (
                      <div title={travelConflict || undefined} className={`flex h-full items-center gap-1 rounded border px-2 ${travelConflict ? "border-rose-400 bg-rose-50 dark:border-rose-600 dark:bg-rose-950" : selected ? "border-emerald-600 bg-emerald-50 dark:border-emerald-500 dark:bg-emerald-950" : "border-neutral-300 bg-white dark:border-neutral-700 dark:bg-neutral-900"}`}>
                        <label className="group relative flex h-full min-w-0 flex-1 cursor-pointer items-center justify-between gap-1.5">
                          <input type="checkbox" disabled={disabled} checked={Boolean(selected)}
                            aria-label={`${entry.scheduleLabel}, ${slot.devaLocationLabel}${slot.custom ? ", custom time" : ""}${travelConflict ? ", travel conflict" : ""}`}
                            aria-describedby={travelConflict ? conflictId : undefined}
                            className="peer sr-only"
                            onChange={(event) => onSlotToggle(slot, event.target.checked)} />
                          <span className="min-w-0 text-xs text-neutral-900 dark:text-neutral-100">
                            <span className="block truncate">{locationLabel}{slot.custom ? ` · ${slot.timeLabel}` : ""}</span>
                            {travelConflict ? <span className="block truncate font-medium text-rose-800 dark:text-rose-300">Travel conflict</span> : slot.custom ? <span className="block text-[11px] text-emerald-700 dark:text-emerald-300">Custom</span> : null}
                            {travelConflict ? <span id={conflictId} className="sr-only">{travelConflict}</span> : null}
                          </span>
                          <span aria-hidden="true" className={`text-sm ${selected ? "text-emerald-700 dark:text-emerald-300" : "text-neutral-400 dark:text-neutral-500"}`}>{selected ? "✓" : "+"}</span>
                          <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-600" />
                        </label>
                        <OfferedPatientsTooltip patients={entry.offeredPatients} scheduleLabel={entry.scheduleLabel} />
                      </div>
                    ) : (
                      <button type="button" className="flex h-full min-w-0 w-full items-center overflow-hidden rounded border border-amber-200 bg-amber-50 px-2 text-left text-xs text-amber-950 hover:bg-amber-100 focus-visible:outline-2 focus-visible:outline-amber-600 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100 dark:hover:bg-amber-900"
                        aria-label={`Booked - ${locationLabel}${entry.bookedPatientName ? ` - ${entry.bookedPatientName}` : ""}, ${entry.scheduleLabel}. Show patient details`}
                        title={entry.bookedPatientName || undefined}
                        aria-haspopup="dialog" onClick={() => setBookedKey(entry.key)}>
                        <span className="shrink-0 whitespace-pre">Booked - {locationLabel}{entry.bookedPatientName ? " - " : ""}</span>
                        {entry.bookedPatientName ? <span className="min-w-0 truncate">{entry.bookedPatientName}</span> : null}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {dates.map((date) => <OfficeToHomeWarning key={date.date} slots={selectedSlots.filter((slot) => slot.date === date.date)} />)}
      {bookedEntry ? <BookedConsultDetails entry={bookedEntry} onClose={() => setBookedKey(null)} /> : null}
    </div>
  );
}
